// Global Instructions Rule Applied!
//
// LPs (FUND MANAGEMENT → LPs, owner 2026-09-14): every LP — the LP Allocation
// row set, one LP definition — with the account's team name and whether it
// is set, so "does every Active LP carry a {{team_name}} greeting" is one
// filter pair (LP status = Active, Team name set = No). Built on the
// canonical grid kit (docs/FRONTEND_DESIGN_PATTERNS.md; LP Allocation is the
// sibling): ViewToolbar ribbon, ViewConfig (columns / filters / sorting from
// cfg.reporting_columns, persisted per user), TableView with infinite
// scroll, and Export to Excel of every row matching the current filters.
import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import type { RootState } from '../../../state_manager/store.ts'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { exportJsonToExcel } from '../../../core/services/utilitiesService.ts'
import ViewToolbar from '../../../core/components/ViewToolbar/index.tsx'
import { ViewConfig } from '../../../core/components/ViewConfig/index.ts'
import type { ViewConfigColumn } from '../../../core/components/ViewConfig/types.ts'
import { getReportingGridColumnsForReportId } from '../../../state_manager/utils/reportingGridColumns.ts'
import { useReportingGridColumnsWithPreferences } from '../../../state_manager/hooks/useReportingGridColumnsWithPreferences.ts'
import { getAddColumnsCatalogForReport } from '../../../state_manager/utils/reportingColumnCatalog.ts'
import { useLpsReportId } from '../../../state_manager/hooks/useLpsReportId.ts'
import { TABLE_LPS } from '../../../state_manager/globals.ts'
import { LpRow, LpRowFields } from '../../../state_manager/data_models/lps_model.ts'
import { useLps } from '../hooks/useLps.ts'
import { fetchLps } from '../services/lpsService.ts'
import { LpsTable } from './LpsTable.tsx'

const EXPORT_PAGE_SIZE = 500

export default function LpsPage() {
  const reportingConfigs = useSelector((state: RootState) => state.globalConfigs?.reporting_configs ?? [])
  const reportingColumnDefaults = useSelector((state: RootState) => state.globalConfigs?.reporting_column_defaults ?? [])
  const reportingColumns = useSelector((state: RootState) => state.globalConfigs?.reporting_columns ?? [])
  const reportId = useLpsReportId()

  const {
    data,
    dataLoading,
    activeFilters,
    activeSorting,
    preferenceRow,
    hasMore,
    currentPage,
    loadingMore,
    filterOptions,
    filterOptionsLoading,
    handleFetch,
    handleSetFilters,
    handleSetSorting,
    handleResetFilters,
    handleLoadMore,
    patchRow,
    defaultTableFilters,
    defaultTableSorting,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault
  } = useLps()

  const gridColumns = useReportingGridColumnsWithPreferences(reportId, preferenceRow)
  const addColumnsCatalog = useMemo(
    () =>
      getAddColumnsCatalogForReport(
        reportId,
        { reporting_columns: reportingColumns, reporting_column_defaults: reportingColumnDefaults },
        { primaryModuleKey: 'lps' }
      ),
    [reportId, reportingColumns, reportingColumnDefaults]
  )
  const catalogFieldNameOrder = useMemo(
    () => [...addColumnsCatalog.deal, ...addColumnsCatalog.related].map((c) => c.valueKey),
    [addColumnsCatalog]
  )
  const defaultTableColumnKeys = useMemo(() => {
    if (!reportId) return null
    return getReportingGridColumnsForReportId(reportId, {
      reporting_configs: reportingConfigs,
      reporting_column_defaults: reportingColumnDefaults,
      reporting_columns: reportingColumns
    }).map((r) => r.valueKey)
  }, [reportId, reportingConfigs, reportingColumnDefaults, reportingColumns])
  const columnByValueKey = useMemo(() => {
    const m = new Map<string, ViewConfigColumn>()
    for (const c of addColumnsCatalog.deal) m.set(c.valueKey, c)
    for (const c of addColumnsCatalog.related) m.set(c.valueKey, c)
    for (const c of gridColumns) if (!m.has(c.valueKey)) m.set(c.valueKey, c)
    return m
  }, [addColumnsCatalog, gridColumns])
  const resolveViewConfigColumn = useCallback((valueKey: string) => columnByValueKey.get(valueKey), [columnByValueKey])
  const columnLayout = useMemo(
    () => ({
      onPersistFieldOrder: persistColumnFieldOrder,
      onResetToDefaults: resetColumnLayoutToDefault,
      resolveColumn: resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    }),
    [persistColumnFieldOrder, resetColumnLayoutToDefault, resolveViewConfigColumn, catalogFieldNameOrder, defaultTableColumnKeys]
  )

  // ── inline cell save (Team name): the field's auto-patch already wrote
  // accounts.team_name; land it on the loaded row and re-derive Yes / No.
  const handleCellSaved = useCallback(
    (accountId: string, updates: Record<string, unknown>) => {
      const patch: Partial<LpRow> = {}
      if (LpRowFields.accounts__team_name in updates) {
        const raw = updates[LpRowFields.accounts__team_name]
        const teamName = typeof raw === 'string' ? raw : null
        patch.accounts__team_name = teamName
        patch.prog__team_name_set = teamName && teamName.trim() ? 'Yes' : 'No'
      }
      if (Object.keys(patch).length > 0) patchRow(accountId, patch)
    },
    [patchRow]
  )

  // ── export: every row matching the current filters, in the grid's order,
  // under the visible columns' titles ─────────────────────────────────────
  const [exporting, setExporting] = useState(false)
  const handleExport = useCallback(async () => {
    setExporting(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const sorting = activeSorting.map((s) => ({ key: s.key, direction: s.direction }))
      const rows: LpRow[] = []
      for (let page = 1; ; page += 1) {
        const res = await fetchLps({ sorting, filters: activeFilters, page, page_size: EXPORT_PAGE_SIZE }, token)
        rows.push(...res.data)
        if (!res.has_more || res.data.length === 0) break
      }
      const exportColumns = gridColumns.map((c) => ({ title: c.columnTitle, key: c.valueKey as keyof LpRow }))
      const sheet = rows.map((r) => {
        const out: Record<string, unknown> = {}
        for (const c of exportColumns) out[c.title] = r[c.key] ?? ''
        return out
      })
      const res = await exportJsonToExcel({ data: sheet, columns: exportColumns.map((c) => c.title), filename: 'LPs.xlsx' }, token)
      if (!res.success) showErrorToast(res.error || 'Export failed')
      else toast.success('Export ready')
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Export failed')
    } finally {
      setExporting(false)
    }
  }, [activeFilters, activeSorting, gridColumns])

  return (
    <div className='h-full overflow-hidden'>
      <div className='w-full h-full flex flex-col bg-white px-2 sm:px-4 pb-2 sm:pb-4 overflow-hidden'>
        <ViewToolbar
          moduleKey={TABLE_LPS}
          searchEnabled={false}
          addButtonEnabled={false}
          activeFilters={activeFilters}
          onRefreshData={(filters) => handleFetch(activeSorting, Array.isArray(filters) ? filters : activeFilters)}
          onFilterChange={(filters) => handleSetFilters(Array.isArray(filters) ? filters : [])}
          defaultFilters={defaultTableFilters}
          onResetFilters={handleResetFilters}
          beforeAddSlot={
            <>
              <ViewConfig
                moduleTitle='LPs'
                columns={gridColumns}
                filterOptions={filterOptions}
                filterOptionsLoading={filterOptionsLoading}
                addColumnsCatalog={addColumnsCatalog}
                addColumnsTabLabels={{ primary: 'LP', related: 'Related' }}
                columnLayout={columnLayout}
                filteringConfig={{
                  currentFilters: activeFilters,
                  defaultFilters: defaultTableFilters,
                  onApplyFilters: async (filters) => handleSetFilters(filters),
                  onResetToDefaults: async () => handleSetFilters(defaultTableFilters)
                }}
                sortingConfig={{
                  currentSorting: activeSorting,
                  defaultSorting: defaultTableSorting,
                  onApplySorting: async (sorting) => handleSetSorting(sorting),
                  onResetToDefaults: async () => handleSetSorting(defaultTableSorting)
                }}
              />
              <Button icon={<FontAwesomeIcon icon={faDownload} />} loading={exporting} onClick={handleExport}>
                Export
              </Button>
            </>
          }
        />

        <div className='flex-1 min-h-0 h-full flex flex-col'>
          <LpsTable
            data={data}
            loading={dataLoading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            currentPage={currentPage}
            gridColumns={gridColumns}
            activeFilters={activeFilters}
            activeSorting={activeSorting}
            filterOptions={filterOptions}
            filterOptionsLoading={filterOptionsLoading}
            onLoadMore={handleLoadMore}
            onFiltersChange={(filters) => handleSetFilters(Object.entries(filters).map(([key, value]) => ({ key, value })))}
            onSortChange={(sortConfig: any) =>
              handleSetSorting(sortConfig ? [{ key: sortConfig.column, direction: sortConfig.direction }] : [])
            }
            onCellSaved={handleCellSaved}
          />
        </div>
      </div>
    </div>
  )
}
